import type { EmissionValueAnsweredUnit } from '@api-client/api'
import { Section } from '@components'
import { CTAPcfAtScale } from '@components/CTAPcfAtScale'
import { formatEmissions } from '@helpers/numbers'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import { Box, Divider, Typography, useTheme } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import type { EmissionResults } from '../Calculator'
import { HighlightedContainer } from '../Form/styles'
import { sum } from '../helpers/calculation'
import { Container } from './styles'

interface ResultsProps {
	data: EmissionResults
	counts: readonly number[]
	unit: EmissionValueAnsweredUnit
}

const ResultItem = ({
	type,
	value,
	count,
	unit,
}: {
	type: keyof EmissionResults
	value: number
	count: number
	unit: EmissionValueAnsweredUnit
}) => {
	const theme = useTheme()
	const { t } = useTranslation()

	return (
		<Box style={{ flexShrink: 2 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				pt={1.5}
			>
				<Box>
					<Typography
						variant="body2"
						color={theme.palette.text.secondary}
						whiteSpace="nowrap"
					>
						{t('calculator.results.stage', { context: type })}
					</Typography>
				</Box>

				<Box textAlign="end">
					<Typography whiteSpace="nowrap" variant="body2">
						{formatEmissions(value, unit, t)}
					</Typography>
				</Box>
			</Box>
			{count > 0 && (
				<Typography
					variant="body2"
					color={theme.palette.text.secondary}
					paddingLeft={2}
					paddingTop={0.5}
				>
					{`... ${count} items included`}
				</Typography>
			)}
		</Box>
	)
}

const Results: FC<ResultsProps> = ({ data, counts, unit }) => {
	const theme = useTheme()
	const { t } = useTranslation()

	return (
		<Container>
			<Section border={false}>
				<Box display="flex" justifyContent="space-between">
					<Box display="flex" flexDirection="column">
						<Typography
							variant="body2"
							fontStyle="italic"
							color={theme.palette.text.secondary}
						>
							{t('calculator.results.total')}
						</Typography>

						<Box display="flex">
							<Typography variant="h1" component="p">
								{formatEmissions(sum(Object.values(data)), unit, t)}
							</Typography>
						</Box>
					</Box>
				</Box>
			</Section>

			<HighlightedContainer mb={2}>
				<Section border={false}>
					{Object.keys(data).map((key, index) => (
						<ResultItem
							key={key}
							type={key as keyof EmissionResults}
							value={data[key as keyof EmissionResults]}
							count={counts[index]}
							unit={unit}
						/>
					))}
				</Section>{' '}
			</HighlightedContainer>

			<Typography variant="h2" pb={2}>
				{t('calculator.whyTrust')}
			</Typography>

			<Box display="flex" alignItems="center" pb={2}>
				<VerifiedUserOutlinedIcon color="primary" />
				<Typography variant="body2" pl={2} color={theme.palette.text.secondary}>
					{t('calculator.resultsInfo')}
				</Typography>
			</Box>
			<Box display="flex" alignItems="center" pb={2}>
				<VerifiedUserOutlinedIcon color="primary" />
				<Typography variant="body2" pl={2} color={theme.palette.text.secondary}>
					{t('calculator.infoNotSaved')}
				</Typography>
			</Box>
			<Box display="flex" alignItems="center" pb={2}>
				<VerifiedUserOutlinedIcon color="primary" />
				<Typography variant="body2" pl={2} color={theme.palette.text.secondary}>
					{t('calculator.onlyTotalShared')}
				</Typography>
			</Box>
			<Divider />
			<CTAPcfAtScale />
		</Container>
	)
}

export { Results }
